// The upload panel (spec/dashboard.md section 9.7): a drop zone and a picker for several files; per file the name (the
// file name without its extension), the group and the kind (from the content type, editable); Upload all posts each
// file in turn as `POST …/assets:upload` with the raw bytes — a command, one commit per file, never staged — with its
// own result row, and the catalog refreshes.
import { useRef, useState, type DragEvent } from 'react';
import { useParams } from 'react-router';
import { ops } from '../../api/operations';
import type { Asset } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useContent } from '../../content/ContentContext';
import { useGame } from '../../shell/GameContext';
import { Button, IconButton } from '../../ui/Button';
import { AlertCircle, CheckCircle2, FolderOpen, Upload, X } from '../../ui/icons';
import { Select, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { ASSET_KINDS, errorText, formatBytes, GroupSelect, kindFromContentType, splitFileName } from './common';

type Status = { state: 'pending' } | { state: 'uploading' } | { state: 'done'; id: string; unchanged: boolean } | { state: 'failed'; message: string };

interface Entry {
  file: File;
  name: string;
  group: string | null;
  kind: string;
  extension: string | undefined;
  status: Status;
}

export function UploadPanel({ open, onClose, groups, defaultGroup }: { open: boolean; onClose: () => void; groups: string[]; defaultGroup: string | null }) {
  const { org, game } = useGame();
  const { branch = '' } = useParams();
  const { client } = useAuth();
  const catalog = useContent();
  const toasts = useToasts();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const add = (files: FileList | File[] | null) => {
    if (!files) return;
    const list = Array.from(files);
    if (list.length === 0) return;
    setEntries((prev) => [
      ...prev,
      ...list.map<Entry>((file) => {
        const { name, extension } = splitFileName(file.name);
        return { file, name, extension, group: defaultGroup, kind: kindFromContentType(file.type), status: { state: 'pending' } };
      }),
    ]);
  };
  const patch = (index: number, p: Partial<Entry>) => setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, ...p } : e)));
  const allGroups = Array.from(new Set([...groups, ...entries.map((e) => e.group).filter((g): g is string => !!g)])).sort();

  const uploadAll = async () => {
    setRunning(true);
    // bytes travel by content hash: a byte-identical upload stores nothing new, and the row says so
    const known = new Set(catalog.assetList.map((a) => a.sha256).filter((s): s is string => !!s));
    let done = 0;
    let failed = 0;
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i]!;
      if (entry.status.state === 'done') continue;
      if (!entry.name.trim()) {
        patch(i, { status: { state: 'failed', message: 'A name is required.' } });
        failed++;
        continue;
      }
      patch(i, { status: { state: 'uploading' } });
      try {
        const result = await client.call(ops.uploadAsset, {
          path: { org, game: game.slug, branch },
          query: { name: entry.name.trim(), kind: entry.kind, group: entry.group ?? undefined, extension: entry.extension },
          rawBody: entry.file,
          contentType: entry.file.type || 'application/octet-stream',
        });
        const asset = result.data as unknown as Asset;
        const unchanged = !!asset.sha256 && known.has(asset.sha256);
        if (asset.sha256) known.add(asset.sha256);
        patch(i, { status: { state: 'done', id: asset.id, unchanged } });
        done++;
      } catch (e) {
        patch(i, { status: { state: 'failed', message: errorText(e) } });
        failed++;
      }
    }
    setRunning(false);
    if (done > 0) catalog.refresh();
    if (failed > 0) toasts.error(`${done} uploaded, ${failed} failed.`);
    else if (done > 0) toasts.success(`${done} file${done === 1 ? '' : 's'} uploaded.`);
  };

  const pending = entries.some((e) => e.status.state === 'pending' || e.status.state === 'failed');
  const close = () => {
    if (running) return;
    setEntries([]);
    onClose();
  };

  return (
    <Modal
      title="Upload assets"
      open={open}
      onClose={close}
      size="l"
      footer={
        <>
          <Button onClick={close} disabled={running}>
            Close
          </Button>
          <Button variant="primary" onClick={() => void uploadAll()} disabled={!pending} busy={running}>
            Upload all
          </Button>
        </>
      }
    >
      <div data-testid="asset-upload" className="asset-upload">
        <div
          className={`asset-dropzone ${over ? 'over' : ''}`}
          onDragOver={(e: DragEvent) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={(e: DragEvent) => {
            e.preventDefault();
            setOver(false);
            add(e.dataTransfer?.files ?? null);
          }}
        >
          <span className="asset-dropzone-icon" aria-hidden="true">
            <Upload />
          </span>
          <p className="asset-dropzone-title">Drop files here, or choose them</p>
          <input
            ref={input}
            type="file"
            multiple
            className="visually-hidden"
            aria-label="Files"
            disabled={running}
            onChange={(e) => {
              add(e.target.files);
              e.target.value = '';
            }}
          />
          <Button size="s" onClick={() => input.current?.click()} disabled={running} icon={<FolderOpen aria-hidden="true" />}>
            Choose files…
          </Button>
          <p className="muted small">Each file is one asset and one commit; byte-identical files are stored once.</p>
        </div>
        {entries.length > 0 && (
          <div className="pane asset-upload-list">
            <div className="pane-head">
              <h3>Files</h3>
              <span className="chip">{entries.length}</span>
              <span className="spacer" />
              <span className="muted small">
                {entries.filter((e) => e.status.state === 'done').length} uploaded · {entries.filter((e) => e.status.state === 'failed').length} failed
              </span>
            </div>
            <table className="grid asset-upload-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Name</th>
                  <th>Group</th>
                  <th>Kind</th>
                  <th>Result</th>
                  <th className="asset-col-remove" aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => {
                  const locked = running || entry.status.state === 'done' || entry.status.state === 'uploading';
                  return (
                    <tr key={i} data-testid={`upload-row-${i}`} className={`grid-row asset-upload-row ${entry.status.state}`}>
                      <td>
                        <span className="asset-file">
                          <span className="asset-file-name" title={entry.file.type || 'application/octet-stream'}>
                            {entry.file.name}
                          </span>
                          <span className="muted small">{formatBytes(entry.file.size)}</span>
                        </span>
                      </td>
                      <td>
                        <TextInput size="s" value={entry.name} disabled={locked} aria-label={`Name of ${entry.file.name}`} onChange={(e) => patch(i, { name: e.target.value })} />
                      </td>
                      <td>
                        <GroupSelect size="s" value={entry.group} groups={allGroups} disabled={locked} label={`Group of ${entry.file.name}`} onChange={(g) => patch(i, { group: g })} />
                      </td>
                      <td>
                        <Select size="s" value={entry.kind} disabled={locked} aria-label={`Kind of ${entry.file.name}`} onChange={(e) => patch(i, { kind: e.target.value })}>
                          {ASSET_KINDS.map((k) => (
                            <option key={k} value={k}>
                              {k}
                            </option>
                          ))}
                        </Select>
                      </td>
                      <td className="asset-upload-status" role="status">
                        <StatusText status={entry.status} />
                      </td>
                      <td className="asset-col-remove">
                        {!locked && (
                          <IconButton label={`Remove ${entry.file.name}`} onClick={() => setEntries((prev) => prev.filter((_, j) => j !== i))}>
                            <X aria-hidden="true" />
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function StatusText({ status }: { status: Status }) {
  switch (status.state) {
    case 'pending':
      return <span className="chip">Ready</span>;
    case 'uploading':
      return (
        <span className="loading-line">
          <span className="spinner" aria-hidden="true" />
          Uploading…
        </span>
      );
    case 'done':
      return (
        <span className="asset-ok">
          <span className={`chip ${status.unchanged ? 'chip-info' : 'chip-success'}`}>
            <CheckCircle2 aria-hidden="true" />
            {status.unchanged ? 'Unchanged bytes' : 'Done'}
          </span>{' '}
          <code>{status.id}</code>
        </span>
      );
    case 'failed':
      return (
        <span className="asset-failed">
          <span className="chip chip-danger">
            <AlertCircle aria-hidden="true" />
            Failed
          </span>{' '}
          <span className="asset-failed-message">{status.message}</span>
        </span>
      );
  }
}
